import { useEffect, useMemo, useState } from "react";
import {
  App as AntdApp,
  Avatar,
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Tabs,
  Tag,
  Typography,
  Upload,
} from "antd";
import type { RcFile } from "antd/es/upload";
import {
  CheckCircleFilled,
  EditOutlined,
  IdcardOutlined,
  InboxOutlined,
  LockOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useGetIdentity } from "@refinedev/core";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import { profileService } from "../../services/auth/profileService";
import type { UserInfo } from "../../types/auth";
import { ADMIN_USER_TYPES } from "../../types/auth";
import { SUPPORTED_LANGUAGES } from "../../i18n/config";
import { usePageTitle } from "../../hooks";
import { PhoneInput } from "../../components/PhoneInput";
import { PageHeader } from "../../components/common/PageHeader";

const { Text } = Typography;

interface FormValues {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  language: string;
}

const LANGUAGE_LABELS: Record<string, string> = {
  es: "🇪🇸 Español (España)",
  en: "🇬🇧 English",
  it: "🇮🇹 Italiano",
  pt: "🇵🇹 Português",
  "es-PE": "🇵🇪 Español (Perú)",
  "es-CL": "🇨🇱 Español (Chile)",
  "es-UY": "🇺🇾 Español (Uruguay)",
};

/** Identifiers of the SVG files in /public/avatars/. */
const AVATAR_PRESETS = [
  "avatar-1",
  "avatar-2",
  "avatar-3",
  "avatar-4",
  "avatar-5",
  "avatar-6",
  "avatar-7",
  "avatar-8",
];

// Mirrors apps/master_data/helpers.py:COUNTRIES so we can render the
// portfolio integer as the country name. Keep this list in sync with the
// backend if new portfolios are added.
const PORTFOLIO_LABELS: Record<number, string> = {
  1: "España",
  2: "Perú",
  3: "Chile",
  4: "Sudáfrica",
  5: "Turquía",
  6: "Egipto",
  7: "Uruguay",
  8: "Portugal",
  9: "Italia",
};

const presetUrl = (id: string) => `/avatars/${id}.svg`;

export const AuditorProfile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { message } = AntdApp.useApp();
  const { data: identity, refetch } = useGetIdentity<UserInfo>();
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);
  // Avatar state lives outside the Form so we can render the preview live.
  // Either `selectedAvatar` (preset id) OR `uploadedFile` (custom upload) is set,
  // never both at the same time. `uploadedPreview` is a blob URL for the preview.
  const [selectedAvatar, setSelectedAvatar] = useState<string>("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedPreview, setUploadedPreview] = useState<string | undefined>();
  // Modal-only draft state — only committed when the user clicks "Apply".
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [draftAvatar, setDraftAvatar] = useState<string>("");
  const [draftFile, setDraftFile] = useState<File | null>(null);
  const [draftPreview, setDraftPreview] = useState<string | undefined>();

  usePageTitle(t("profile.title"));

  useEffect(() => {
    if (!identity) return;
    form.setFieldsValue({
      first_name: identity.first_name || "",
      last_name: identity.last_name || "",
      email: identity.email || "",
      phone_number: identity.app_user?.phone_number || "",
      language:
        identity.app_user?.language || identity.app_user?.effective_language || "",
    });
    setSelectedAvatar(identity.app_user?.avatar_choice || "");
  }, [identity, form]);

  // Free blob URLs we created locally to avoid leaks on unmount / change.
  useEffect(() => {
    return () => {
      if (uploadedPreview?.startsWith("blob:")) URL.revokeObjectURL(uploadedPreview);
      if (draftPreview?.startsWith("blob:")) URL.revokeObjectURL(draftPreview);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watch first/last name to compose a live preview of the display name.
  const firstName = Form.useWatch("first_name", form);
  const lastName = Form.useWatch("last_name", form);
  const composedName = useMemo(
    () => [firstName, lastName].map((s) => (s || "").trim()).filter(Boolean).join(" "),
    [firstName, lastName],
  );

  const onSubmit = async (values: FormValues) => {
    setSubmitting(true);
    try {
      // The picker enforces "either preset OR upload, never both".
      // If the user uploaded a file, send the avatar binary and clear the
      // preset; otherwise just send the preset id (or empty to clear).
      const payload: any = { ...values };
      if (uploadedFile) {
        payload.avatar = uploadedFile;
        payload.avatar_choice = "";
      } else {
        payload.avatar_choice = selectedAvatar;
      }
      await profileService.updateProfile(payload);
      message.success(t("profile.save_success"));
      // After a successful upload, clear the local file state so the next
      // save doesn't re-upload the same image.
      setUploadedFile(null);
      if (uploadedPreview?.startsWith("blob:")) {
        URL.revokeObjectURL(uploadedPreview);
        setUploadedPreview(undefined);
      }
      await refetch?.();
    } catch (err: any) {
      console.error("Update profile error:", err);
      message.error(t("profile.save_error"));
    } finally {
      setSubmitting(false);
    }
  };

  // Resolve the avatar shown on the page.
  // 1) Pending upload preview (if any).
  // 2) Selected preset.
  // 3) Whatever the backend says (avatar_url).
  const avatarUrl =
    uploadedPreview ??
    (selectedAvatar
      ? presetUrl(selectedAvatar)
      : identity?.app_user?.avatar_url ?? undefined);

  const openAvatarModal = () => {
    setDraftAvatar(selectedAvatar);
    setDraftFile(null);
    setDraftPreview(undefined);
    setAvatarModalOpen(true);
  };

  const applyAvatar = () => {
    // Commit the draft into the page state.
    if (draftFile) {
      // Upload path — kill any selected preset.
      setUploadedFile(draftFile);
      if (uploadedPreview?.startsWith("blob:")) URL.revokeObjectURL(uploadedPreview);
      setUploadedPreview(draftPreview);
      setSelectedAvatar("");
    } else {
      // Preset path — drop any pending upload.
      setSelectedAvatar(draftAvatar);
      setUploadedFile(null);
      if (uploadedPreview?.startsWith("blob:")) URL.revokeObjectURL(uploadedPreview);
      setUploadedPreview(undefined);
    }
    setAvatarModalOpen(false);
  };

  const handleBeforeUpload = (file: RcFile) => {
    if (!file.type?.startsWith("image/")) {
      message.error(t("profile.avatar_upload_wrong_type"));
      return Upload.LIST_IGNORE;
    }
    if (file.size > 2 * 1024 * 1024) {
      message.error(t("profile.avatar_upload_too_large"));
      return Upload.LIST_IGNORE;
    }
    if (draftPreview?.startsWith("blob:")) URL.revokeObjectURL(draftPreview);
    setDraftFile(file);
    setDraftPreview(URL.createObjectURL(file));
    setDraftAvatar("");
    return false; // never auto-upload — we send it with the form submit
  };

  const userTypeDisplay = identity?.app_user?.user_type_display;
  const userTypeIsManager = identity?.app_user?.user_type === 4;

  return (
    <Card>
      <PageHeader
        icon={<IdcardOutlined />}
        title={t("profile.title")}
        subtitle={t("profile.subtitle")}
        extra={
          <Button
            icon={<LockOutlined />}
            // El componente lo reutiliza también el área admin en
            // /admin/profile — derivamos el path relativo desde el
            // user_type para que el cambio de contraseña navegue a
            // la ruta correcta (ambos espacios tienen `.../password`
            // registrado bajo su Route guard).
            onClick={() => {
              const isAdmin = (ADMIN_USER_TYPES as readonly number[]).includes(
                identity?.app_user?.user_type ?? -1,
              );
              navigate(
                isAdmin ? "/admin/profile/password" : "/auditor/profile/password",
              );
            }}
          >
            {t("profile.change_password")}
          </Button>
        }
      />

      <Row gutter={[24, 24]}>
        {/* Left column: avatar picker + summary */}
        <Col xs={24} md={8}>
          {/* Summary card */}
          <Card
            type="inner"
            title={
              <Text strong>{t("profile.summary", "Resumen")}</Text>
            }
            style={{ marginBottom: 16 }}
            styles={{ body: { padding: 20 } }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 12,
              }}
            >
              <Avatar
                size={104}
                src={avatarUrl}
                icon={!avatarUrl ? <UserOutlined /> : undefined}
                style={{ border: "3px solid #fafafa", boxShadow: "0 2px 12px rgba(0,0,0,.05)" }}
              />
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 16, fontWeight: 600 }}>
                  {composedName || identity?.app_user?.name || identity?.username || "—"}
                </div>
                {identity?.username && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    @{identity.username}
                  </Text>
                )}
              </div>
              {userTypeDisplay && (
                <Tag color={userTypeIsManager ? "geekblue" : "blue"}>
                  {userTypeDisplay}
                </Tag>
              )}

              <Button
                icon={<EditOutlined />}
                onClick={openAvatarModal}
                size="small"
                style={{ marginTop: 4 }}
              >
                {t("profile.avatar_change")}
              </Button>
            </div>

            <div style={{ borderTop: "1px solid #f0f0f0", marginTop: 18, paddingTop: 14 }}>
              <SummaryRow
                label={t("profile.code")}
                value={identity?.app_user?.code}
              />
              <SummaryRow
                label={t("profile.company")}
                value={identity?.app_user?.audit_company_name}
              />
              <SummaryRow
                label={t("profile.portfolio")}
                value={renderPortfolios(identity?.app_user)}
              />
            </div>
          </Card>
        </Col>

        {/* Right column: editable form */}
        <Col xs={24} md={16}>
          <Card type="inner" title={<Text strong>{t("profile.personal_data")}</Text>}>
            <Form form={form} layout="vertical" onFinish={onSubmit} requiredMark={false}>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={t("profile.first_name")}
                    name="first_name"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder={t("profile.ph_first_name")} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={t("profile.last_name")} name="last_name">
                    <Input placeholder={t("profile.ph_last_name")} />
                  </Form.Item>
                </Col>
              </Row>

              {/* Read-only composed display name — auto-filled from first + last */}
              <Form.Item label={t("profile.name_app")} style={{ marginBottom: 18 }}>
                <Input
                  value={composedName}
                  readOnly
                  variant="filled"
                  style={{ color: "#595959" }}
                  placeholder={t("profile.ph_name_app")}
                />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={t("profile.email")}
                    name="email"
                    rules={[{ type: "email" }]}
                  >
                    <Input placeholder={t("profile.ph_email")} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={t("profile.phone")} name="phone_number">
                    <PhoneInput
                      placeholder={t("profile.ph_phone")}
                      countryFilter={[
                        "ES", "IT", "PT", "ZA", "PE", "CL", "UY",
                        "EG", "CY", "TR", "EC", "MX",
                      ]}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label={t("profile.language")}
                name="language"
                help={t("profile.language_help")}
              >
                <Select
                  placeholder={t("profile.ph_language")}
                  options={SUPPORTED_LANGUAGES.map((code) => ({
                    value: code,
                    label: LANGUAGE_LABELS[code] ?? code,
                  }))}
                  allowClear
                />
              </Form.Item>

              <Button type="primary" htmlType="submit" loading={submitting}>
                {t("profile.save")}
              </Button>
            </Form>
          </Card>
        </Col>
      </Row>

      <AvatarModal
        open={avatarModalOpen}
        onCancel={() => setAvatarModalOpen(false)}
        onApply={applyAvatar}
        draftAvatar={draftAvatar}
        setDraftAvatar={(id) => {
          setDraftAvatar(id);
          if (draftPreview?.startsWith("blob:")) URL.revokeObjectURL(draftPreview);
          setDraftFile(null);
          setDraftPreview(undefined);
        }}
        draftPreview={draftPreview}
        beforeUpload={handleBeforeUpload}
        t={t}
      />
    </Card>
  );
};

/**
 * Compose the portfolio label shown in the summary card.
 * - Auditor (type 5): the country of their main `portfolio`.
 * - Auditor Manager (type 4): the countries of `allowed_portfolios` joined
 *   by commas, falling back to the main portfolio if the manager has none
 *   assigned yet.
 */
const renderPortfolios = (
  appUser: UserInfo["app_user"] | undefined,
): string | undefined => {
  if (!appUser) return undefined;
  const codes =
    appUser.user_type === 4 && (appUser.allowed_portfolios ?? []).length > 0
      ? appUser.allowed_portfolios!
      : [appUser.portfolio];
  return codes.map((c) => PORTFOLIO_LABELS[c] ?? String(c)).join(", ");
};

const SummaryRow = ({
  label,
  value,
}: {
  label: string;
  value: string | number | null | undefined;
}) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "6px 0",
      fontSize: 13,
    }}
  >
    <Text type="secondary">{label}</Text>
    <Text style={{ fontWeight: 500 }}>{value || "—"}</Text>
  </div>
);

interface AvatarModalProps {
  open: boolean;
  onCancel: () => void;
  onApply: () => void;
  draftAvatar: string;
  setDraftAvatar: (id: string) => void;
  draftPreview: string | undefined;
  beforeUpload: (file: RcFile) => boolean | string;
  t: (key: string) => string;
}

const AvatarModal = ({
  open,
  onCancel,
  onApply,
  draftAvatar,
  setDraftAvatar,
  draftPreview,
  beforeUpload,
  t,
}: AvatarModalProps) => {
  const hasSelection = !!draftAvatar || !!draftPreview;

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      onOk={onApply}
      okButtonProps={{ disabled: !hasSelection }}
      okText={t("profile.avatar_apply")}
      cancelText={t("common.cancel")}
      title={t("profile.avatar_modal_title")}
      width={520}
      destroyOnClose
    >
      <Tabs
        items={[
          {
            key: "presets",
            label: t("profile.avatar_tab_presets"),
            children: (
              <>
                <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 12 }}>
                  {t("profile.avatar_pick_help")}
                </Text>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: 12,
                  }}
                >
                  {AVATAR_PRESETS.map((id) => {
                    const isSelected = id === draftAvatar;
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setDraftAvatar(id)}
                        style={{
                          position: "relative",
                          padding: 6,
                          border: isSelected ? "2px solid #FF8C00" : "2px solid #f0f0f0",
                          borderRadius: 12,
                          background: "#fff",
                          cursor: "pointer",
                          lineHeight: 0,
                          transition: "border-color 100ms ease-out",
                        }}
                        aria-pressed={isSelected}
                        aria-label={id}
                      >
                        <Avatar size={64} src={presetUrl(id)} />
                        {isSelected && (
                          <CheckCircleFilled
                            style={{
                              position: "absolute",
                              right: 4,
                              top: 4,
                              fontSize: 18,
                              color: "#FF8C00",
                              background: "#fff",
                              borderRadius: 999,
                            }}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            ),
          },
          {
            key: "upload",
            label: t("profile.avatar_tab_upload"),
            children: (
              <>
                <Upload.Dragger
                  accept="image/*"
                  multiple={false}
                  showUploadList={false}
                  beforeUpload={beforeUpload}
                  style={{ padding: 8 }}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined style={{ fontSize: 36, color: "#FF8C00" }} />
                  </p>
                  <p className="ant-upload-text" style={{ fontSize: 14, fontWeight: 500 }}>
                    {t("profile.avatar_tab_upload")}
                  </p>
                  <p className="ant-upload-hint" style={{ fontSize: 12 }}>
                    {t("profile.avatar_upload_help")}
                  </p>
                </Upload.Dragger>
                {draftPreview && (
                  <div style={{ marginTop: 16, textAlign: "center" }}>
                    <Avatar
                      size={96}
                      src={draftPreview}
                      style={{ border: "2px solid #FF8C00" }}
                    />
                  </div>
                )}
              </>
            ),
          },
        ]}
      />
    </Modal>
  );
};

export default AuditorProfile;
